import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faArrowRight, faMedal, faGift, faRocket } from '@fortawesome/free-solid-svg-icons';
import Countdown from '../components/Competitions/Countdown';
import Participants from '../components/Competitions/Participants';
import './CompetitionTeaser.css';

const API = process.env.REACT_APP_API_BASE_URL;
const PLACE = { 1: '1st place', 2: '2nd place', 3: '3rd place' };

/* Student dashboard: the open challenge (or the next one, or the latest winners),
   with a live countdown, who already joined, and my last trophy.
   Shows nothing when there is no competition at all. */
const CompetitionTeaser = () => {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API}/api/student/competitions`, { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } })
      .then((res) => setData(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const comps = data?.competitions || [];
  const open = comps.find((c) => c.status === 'open');
  const next = [...comps].filter((c) => c.status === 'upcoming').sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)))[0];
  const voting = comps.find((c) => c.status === 'judging');
  const done = comps.find((c) => c.status === 'finished');
  const c = open || next || voting || done;
  if (!c) return null;
  const win = data.wins?.[0];

  const startDay = (s) => {
    const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : '';
  };
  const live = open || next; // challenge shown with people + countdown
  const people = live?.participants || [];

  const kicker = open ? '🔥 Challenge open now' : next ? '🚀 New challenge coming' : voting ? '⭐ Teachers are voting' : '🏆 Latest challenge';
  const cta = open
    ? open.myEntry
      ? 'See my project'
      : 'Join the challenge'
    : next
    ? next.joined
      ? 'See the challenge'
      : 'Read the challenge & join'
    : voting
    ? 'See the projects'
    : 'See the winners';

  return (
    <section className={`rmsct is-${c.status}`} aria-label="Competition">
      <div className="rmsct-main">
        <span className="rmsct-icon" aria-hidden="true">
          <FontAwesomeIcon icon={next && !open ? faRocket : faTrophy} />
        </span>
        <div className="rmsct-body">
          <small>{kicker}</small>
          <b>{c.title}</b>
          {c.prize && (open || next) && (
            <span className="rmsct-prize">
              <FontAwesomeIcon icon={faGift} /> {c.prize}
            </span>
          )}
          {open?.myEntry && <span className="rmsct-in">Your project is in. Good luck! 🍀</span>}
          {!open && next?.joined && <span className="rmsct-in">You accepted the challenge! Send your project from {startDay(next.startDate)} 🚀</span>}
          {!open && voting && <span>The winners are announced soon.</span>}
          {!open && !next && !voting && <span>See the winning projects.</span>}
        </div>
      </div>

      {(open || next) && (
        <div className="rmsct-timer">
          <Countdown to={open ? open.endsAt : next.startsAt} label={open ? 'Time left to send your project' : 'Starts in'} size="sm" onDone={load} />
        </div>
      )}

      <div className="rmsct-foot">
        {live && people.length > 0 ? (
          <div className="rmsct-joined">
            <Participants list={people} max={6} compact />
            <span>
              <b>{people.length}</b> {people.length === 1 ? 'student is' : 'students are'} taking part
            </span>
          </div>
        ) : live ? (
          <span className="rmsct-first">Be the first to join! 🚀</span>
        ) : (
          <span />
        )}
        {/* the student reads the challenge on its page, and accepts it there */}
        <Link to="/student/competitions" className="rmsct-btn">
          {cta} <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>

      {win && (
        <p className="rmsct-win">
          <FontAwesomeIcon icon={faMedal} /> You won <b>{PLACE[win.rank]}</b> in {win.competition}!
        </p>
      )}
    </section>
  );
};

export default CompetitionTeaser;
